import type { PayloadStrategy } from '@/pages/components/audit/adjustment/adapters/types';
import {
  BankWorkingPaper,
  CommonMaterialTestWorkingPaper, CommonTransactionWorkingPaper,
  CommonWorkingPaper, DebtTransactionWorkingPaper, FeesTransactionWorkingPaper,
  DebtFeesTransactionWorkingPaper, CommonInvestmentWorkingPaper
} from "@/services/auditService/interface/common";

export const basicStrategy: PayloadStrategy = {
  buildPayload: ({ prev, amount }) => {
    const next: any = { ...(prev || {}) };
    next.adjustment = Number(amount || 0) || 0;
    if (typeof next.perClient === 'number') next.finalAmount = Number(next.perClient || 0) + Number(amount || 0);
    if (typeof next.perSales === 'number') next.finalAmount = Number(next.perSales || 0) + Number(amount || 0);
    return next;
  },
  readAmount: (payload: any) => Number((payload || {})?.adjustment ?? 0) || 0,
};

// Common Strategy: ensure payload conforms to ConsultancyFeeWorkingPaper.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const commonStrategy: PayloadStrategy = {
  buildPayload: ({ prev, ctx }) => {
    const p = (prev || {}) as Partial<CommonWorkingPaper>;
    const annualId = ctx?.annualId;
    
    // Helper function to add annualId to each row
    const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
      if (!Array.isArray(rows)) return rows;
      return rows.map(row => ({
        ...row,
        annualId: annualId
      }));
    };
    
    const commonRow = addAnnualId(Array.isArray(p.commonRow) ? p.commonRow : []);
    
    const next: any = {};
    if (commonRow.length > 0) next.commonRow = commonRow;
    
    return next;
  },
  // Return undefined to let registry fall back to entry.adjustmentAmount
  readAmount: (payload: any) => {
    const n = Number((payload || {}).adjustment);
    return Number.isFinite(n) ? n : (undefined as any);
  },
}

// Common Test Strategy: ensure payload conforms to ConsultancyFeeWorkingPaper.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const commonMaterialTestStrategy: PayloadStrategy = {
  buildPayload: ({ prev, ctx }) => {
    const p = (prev || {}) as Partial<CommonMaterialTestWorkingPaper>;
    const annualId = ctx?.annualId;
    
    // Helper function to add annualId to each row
    const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
      if (!Array.isArray(rows)) return rows;
      return rows.map(row => ({
        ...row,
        annualId: annualId
      }));
    };
    
    const commonRow = addAnnualId(Array.isArray(p.commonRow) ? p.commonRow : []);
    const relatedRows = addAnnualId(Array.isArray(p.relatedRows) ? p.relatedRows : []);
    const testRow = addAnnualId(Array.isArray(p.testRow) ? p.testRow : []);
    
    const next: any = {};
    if (commonRow.length > 0) next.commonRow = commonRow;
    if (relatedRows.length > 0) next.relatedRows = relatedRows;
    if (testRow.length > 0) next.testRow = testRow;
    
    return next;
  },
  // Return undefined to let registry fall back to entry.adjustmentAmount
  readAmount: (payload: any) => {
    const n = Number((payload || {}).adjustment);
    return Number.isFinite(n) ? n : (undefined as any);
  },
}

// Common Transaction Strategy: ensure payload conforms to DonationWorkingPaper.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const commonTransactionStrategy: PayloadStrategy = {
  buildPayload: ({ prev, ctx }) => {
    const p = (prev || {}) as Partial<CommonTransactionWorkingPaper>;
    const annualId = ctx?.annualId;
    
    // Helper function to add annualId to each row
    const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
      if (!Array.isArray(rows)) return rows;
      return rows.map(row => ({
        ...row,
        annualId: annualId
      }));
    };
    
    const commonRow = addAnnualId(Array.isArray(p.commonRow) ? p.commonRow : []);
    const relatedRows = addAnnualId(Array.isArray(p.relatedRows) ? p.relatedRows : []);
    
    const next: any = {};
    if (commonRow.length > 0) next.commonRow = commonRow;
    if (relatedRows.length > 0) next.relatedRows = relatedRows;
    
    return next;
  },
  // Return undefined to let registry fall back to entry.adjustmentAmount
  readAmount: (payload: any) => {
    const n = Number((payload || {}).adjustment);
    return Number.isFinite(n) ? n : (undefined as any);
  },
};

// Bank Strategy: ensure payload conforms to ConsultancyFeeWorkingPaper.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const bankStrategy: PayloadStrategy = {
  buildPayload: ({ prev, ctx }) => {
    const p = (prev || {}) as Partial<BankWorkingPaper>;
    const annualId = ctx?.annualId;
    
    // Helper function to add annualId to each row
    const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
      if (!Array.isArray(rows)) return rows;
      return rows.map(row => ({
        ...row,
        annualId: annualId
      }));
    };
    
    const bankRow = addAnnualId(Array.isArray(p.bankRow) ? p.bankRow : []);
    const commonRow = addAnnualId(Array.isArray(p.commonRow) ? p.commonRow : []);
    
    const next: any = {};
    if (bankRow.length > 0) next.bankRow = bankRow;
    if (commonRow.length > 0) next.commonRow = commonRow;
    
    return next;
  },
  // Return undefined to let registry fall back to entry.adjustmentAmount
  readAmount: (payload: any) => {
    const n = Number((payload || {}).adjustment);
    return Number.isFinite(n) ? n : (undefined as any);
  },
}

// Fees Transaction Strategy: ensure payload conforms to ConsultancyFeeWorkingPaper.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const feesTransactionStrategy: PayloadStrategy = {
  buildPayload: ({ prev, ctx }) => {
    const p = (prev || {}) as Partial<FeesTransactionWorkingPaper>;
    const annualId = ctx?.annualId;
    
    // Helper function to add annualId to each row
    const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
      if (!Array.isArray(rows)) return rows;
      return rows.map(row => ({
        ...row,
        annualId: annualId
      }));
    };
    
    const feesRow = addAnnualId(Array.isArray(p.feesRow) ? p.feesRow : []);
    const relatedRows = addAnnualId(Array.isArray(p.relatedRows) ? p.relatedRows : []);
    
    const next: any = {};
    if (feesRow.length > 0) next.feesRow = feesRow;
    if (relatedRows.length > 0) next.relatedRows = relatedRows;
    
    return next;
  },
  // Return undefined to let registry fall back to entry.adjustmentAmount
  readAmount: (payload: any) => {
    const n = Number((payload || {}).adjustment);
    return Number.isFinite(n) ? n : (undefined as any);
  },
}

// Debt Transaction Strategy: ensure payload conforms to ConsultancyFeeWorkingPaper.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const debtTransactionStrategy: PayloadStrategy = {
  buildPayload: ({ prev, ctx }) => {
    const p = (prev || {}) as Partial<DebtTransactionWorkingPaper>;
    const annualId = ctx?.annualId;
    
    // Helper function to add annualId to each row
    const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
      if (!Array.isArray(rows)) return rows;
      return rows.map(row => ({
        ...row,
        annualId: annualId
      }));
    };
    
    const debtRow = addAnnualId(Array.isArray(p.debtRow) ? p.debtRow : []);
    const relatedRows = addAnnualId(Array.isArray(p.relatedRows) ? p.relatedRows : []);
    
    const next: any = {};
    if (debtRow.length > 0) next.debtRow = debtRow;
    if (relatedRows.length > 0) next.relatedRows = relatedRows;
    
    return next;
  },
  // Return undefined to let registry fall back to entry.adjustmentAmount
  readAmount: (payload: any) => {
    const n = Number((payload || {}).adjustment);
    return Number.isFinite(n) ? n : (undefined as any);
  },
}

// Merged Debt/Fees Transaction Strategy (uses debtFeeRow + relatedRows)
export const debtFeesTransactionStrategy: PayloadStrategy = {
  buildPayload: ({ prev, ctx }) => {
    const p = (prev || {}) as Partial<DebtFeesTransactionWorkingPaper>;
    const annualId = ctx?.annualId;
    const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
      if (!Array.isArray(rows)) return rows;
      return rows.map(row => ({...row, annualId}));
    };
    const debtFeeRow = addAnnualId(Array.isArray(p.debtFeeRow) ? p.debtFeeRow : []);
    const relatedRows = addAnnualId(Array.isArray(p.relatedRows) ? p.relatedRows : []);
    const next: any = {};
    if (debtFeeRow.length > 0) next.debtFeeRow = debtFeeRow;
    if (relatedRows.length > 0) next.relatedRows = relatedRows;
    if (p.addBackEnabled === true) next.addBackEnabled = true;
    return next;
  },
  readAmount: (payload: any) => {
    const n = Number((payload || {}).adjustment);
    return Number.isFinite(n) ? n : (undefined as any);
  },
}

// Investment Subsidiary / Associate / Trusts (G1–G3): IA-shaped intangibleRows (+ optional principal).
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const InvestmentSubsidiaryStrategy: PayloadStrategy = {
  buildPayload: ({prev, ctx}) => {
    const p = (prev || {}) as Partial<CommonInvestmentWorkingPaper> & Record<string, any>;
    const annualId = ctx?.annualId;

    const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
      if (!Array.isArray(rows)) return rows;
      return rows.map(row => ({
        ...row,
        annualId: annualId,
      }));
    };

    const intangibleRows = addAnnualId(Array.isArray(p.intangibleRows) ? p.intangibleRows : []);
    const principalSubsidiaryRow = addAnnualId(
      Array.isArray(p.principalSubsidiaryRow) ? p.principalSubsidiaryRow : [],
    );
    // Legacy vertical tables — only when intangibleRows absent (stale drafts)
    const costRow = addAnnualId(Array.isArray(p.costRow) ? p.costRow : []);
    const impairmentLossRow = addAnnualId(Array.isArray(p.impairmentLossRow) ? p.impairmentLossRow : []);

    const next: any = {};
    if (intangibleRows.length > 0) {
      next.intangibleRows = intangibleRows;
    } else {
      if (costRow.length > 0) next.costRow = costRow;
      if (impairmentLossRow.length > 0) next.impairmentLossRow = impairmentLossRow;
    }
    if (principalSubsidiaryRow.length > 0) next.principalSubsidiaryRow = principalSubsidiaryRow;
    // Schedule-level / slim toPaper may carry top-level nbvCfAdj (IA parity)
    if (Math.abs(Number(p.nbvCfAdj) || 0) >= 0.001) {
      next.nbvCfAdj = p.nbvCfAdj;
    }
    if (p.adjustment != null && Number.isFinite(Number(p.adjustment))) {
      next.adjustment = Number(p.adjustment);
    }

    return next;
  },
  // Return undefined to let registry fall back to entry.adjustmentAmount
  readAmount: (payload: any) => {
    const n = Number((payload || {}).adjustment);
    return Number.isFinite(n) ? n : (undefined as any);
  },
}